import { msg } from '@lingui/core/macro';
import { sendApiRequest } from 'utils/network';
import { user } from 'utils/auth/user';
import { Cache } from 'utils/cache';
import { getLinks, sharingSnapshot, userSession } from 'state/session';
import { jsonSafeParse } from 'utils/objects';
import { TNerdDevice, TNerdDevicesApiResponse } from '../../hooks/nerd/types';
import { showToast } from 'state/toast';
import { createPollingController, PollingController } from 'utils/network/polling';
import { setStorageItem, getStorageItem } from 'utils/ui/storage';

const cache = new Cache();

let sessionId = '';
let pollingController: PollingController | null = null;

/**
 * Keep the broadcast session alive by sending periodic requests
 */
const keepAlive = async (): Promise<void> => {
  if (!sessionId) {
    throw new Error('No active broadcast session');
  }

  const contextToken: string = cache.getValue<string>('nerd:context-token');
  const snapshot = sharingSnapshot();
  const renderer = jsonSafeParse<TNerdDevice>(snapshot.streamingDevice);

  if (!renderer.thingName) {
    throw new Error('Invalid renderer - no thingName available');
  }

  const response = await sendApiRequest<TNerdDevicesApiResponse<null>>(
    `/broadcast/${sessionId}?dealerId=${user.dealerId}&brand=${userSession().get('brand')}&market=${userSession().get('market')}`,
    {
      method: 'PUT',
      body: {
        deviceId: renderer.thingName,
      },
    },
    true,
    contextToken
  );

  if (response && response.contextToken) {
    cache.setValue('nerd:context-token', response.contextToken);
  }
};

/**
 * Start polling for keep-alive requests
 */
const startPolling = (): void => {
  if (pollingController) {
    pollingController.destroy();
  }

  pollingController = createPollingController({
    fetchFunction: keepAlive,
    interval: 60 * 1000,
    runImmediately: false,
    onError: (error) => {
      console.error('Error in broadcast keep-alive polling:', error);
    },
  });

  pollingController.start();
};

/**
 * Stop polling for keep-alive requests
 */
const stopPolling = (): void => {
  if (pollingController) {
    pollingController.stop();
    pollingController.destroy();
    pollingController = null;
  }
};

/**
 * Start broadcasting to a NERD device
 */
export const startBroadcast = async (): Promise<void> => {
  const session = userSession().get();
  const contextToken: string = cache.getValue<string>('nerd:context-token');
  const { sharingCta } = getLinks();

  if (session.sharingType !== 'broadcast') {
    console.error('Not a broadcast session');
    return;
  }

  const renderer = jsonSafeParse<TNerdDevice>(session.streamingDevice);

  if (!renderer.thingName || !sharingCta) {
    console.error('Invalid renderer or sharingCta is not defined');
    return;
  }

  try {
    const response = await sendApiRequest<TNerdDevicesApiResponse<string>>(
      `/broadcast?dealerId=${user.dealerId}&brand=${session.brand}&market=${session.market}`,
      {
        method: 'POST',
        body: {
          deviceId: renderer.thingName,
          url: sharingCta,
        },
      },
      true,
      contextToken
    );

    if (response) {
      if (!contextToken) {
        cache.setValue('nerd:context-token', response.contextToken);
      }
      sessionId = response.sessionId;
      /** Store the sessionId in session storage to be able to resume sessions */
      setStorageItem('sessionStorage', 'nerd:session-id', sessionId);
      startPolling();
    }
  } catch (error) {
    showToast({
      title: msg`Error starting broadcast`,
      message: msg`Please try again in a few minutes`,
      type: 'error',
      notificationId: 'BROADCAST_START_ERROR',
    });
    console.error('Error starting broadcast:', error);
    userSession().queue({ customerAllowed: false, snapshot: '{}' });
  }
};

/**
 * Resume broadcast sessions if it was already started
 */
export const resumeBroadcast = (): void => {
  /** Update sessionId in case it misses */
  const existingSessionId = getStorageItem('sessionStorage', 'nerd:session-id');
  if (!sessionId && !existingSessionId) {
    return;
  }
  sessionId = sessionId || existingSessionId;

  /** Resume polling if not already active */
  if (pollingController && pollingController.isPolling()) {
    return;
  }
  startPolling();
};

/**
 * Stop broadcasting to a NERD device
 */
export const stopBroadcast = async ({ keepAlive = false }: { keepAlive?: boolean } = {}): Promise<void> => {
  const contextToken: string = cache.getValue<string>('nerd:context-token');
  const snapshot = sharingSnapshot();

  if (!sessionId) {
    console.error('No active broadcast session to stop');
    return;
  }

  if (snapshot.sharingType !== 'broadcast') {
    console.error('Not a broadcast session');
    return;
  }

  const renderer = jsonSafeParse<TNerdDevice>(snapshot.streamingDevice);
  if (!renderer?.thingName) {
    console.error('Invalid renderer - no thingName available');
    return;
  }

  try {
    const response = await sendApiRequest<TNerdDevicesApiResponse<null>>(
      `/broadcast/${sessionId}?dealerId=${user.dealerId}&brand=${snapshot.brand}&market=${snapshot.market}`,
      { method: 'DELETE', body: { deviceId: renderer.thingName }, keepalive: keepAlive },
      true,
      contextToken
    );

    if (response && response.contextToken) {
      cache.setValue('nerd:context-token', response.contextToken);
    }
  } catch (error) {
    console.error('Error stopping broadcast:', error);
  } finally {
    stopPolling();
    sessionId = '';
    setStorageItem('sessionStorage', 'nerd:session-id', '');
    userSession().setValues({ streamingDevice: '' });
  }
};

/**
 * Clean up any resources used by the broadcast manager
 */
export const cleanupBroadcast = (): void => {
  stopPolling();
  sessionId = '';
};
